import React, { useEffect, useState, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { billingErrorMessage } from '../../../../utils/billingErrors';
import PaymentMethodModal from './PaymentMethodModal';
import { BillingRow, BillingStatus } from './billingUi';

const date = value => new Date(value).toLocaleDateString('sv-SE');

export default function PaymentMethodPanel({ companyId, entitlements, onRefresh, onPortal, portalBusy }) {
    const [options,setOptions] = useState(null);
    const [details,setDetails] = useState({});
    const [choosing,setChoosing] = useState(false);
    const [busy,setBusy] = useState(false);
    const [error,setError] = useState('');
    const [reload,setReload] = useState(0);
    const current = useRef(companyId); current.current = companyId;
    const editing = useRef(choosing); editing.current = choosing;
    useEffect(() => () => { current.current = null; }, []);
    useEffect(() => { setOptions(null); setError(''); setChoosing(false); setBusy(false); }, [companyId]);
    useEffect(() => {
        let live = true;
        axios.get(getApiUrl(`${companyId}/subscription/payment-options`)).then(({data}) => {
            if (live) { setOptions(data); if (!editing.current) setDetails(data.billingDetails || {}); }
        }).catch(() => { if (live) setError('Kunde inte hämta betalsätten.'); });
        return () => { live = false; };
    }, [companyId,reload,entitlements]);
    const choose = async paymentMethod => {
        if (busy) return;
        setBusy(true); setError('');
        try {
            const {data} = await axios.post(getApiUrl(`${companyId}/subscription/payment-method`), {paymentMethod,...(paymentMethod === 'invoice' ? {billingDetails:details} : {})});
            if (current.current !== companyId) return;
            if (data.url) { window.location.assign(data.url); return; }
            setChoosing(false); setReload(value => value + 1);
            toast.success(paymentMethod === 'invoice' ? 'Fakturabetalning är valt.' : 'Betalsättet är uppdaterat.');
            await onRefresh?.();
        } catch (failure) { if (current.current === companyId) setError(billingErrorMessage(failure, 'Kunde inte ändra betalsätt. Försök igen.')); }
        finally { if (current.current === companyId) setBusy(false); }
    };

    const trialEnd = entitlements?.trialEndsAt ? new Date(entitlements.trialEndsAt) : null;
    const remaining = trialEnd ? trialEnd - new Date() : 0;
    const inTrial = remaining > 0;
    const endingSoon = inTrial && remaining <= 7 * 86400000;
    const locked = !!entitlements?.cancelAtPeriodEnd;
    const invoiceNow = options?.current === 'invoice';
    // Card can always be added or replaced; invoice is offered only as a change.
    const choices = (options?.methods || []).filter(method => method === 'card' || !invoiceNow);
    const single = choices.length === 1 ? choices[0] : null;
    const actionLabel = single === 'card' ? (invoiceNow ? 'Byt till kortbetalning' : options?.ready ? 'Uppdatera kort' : 'Lägg till kort')
        : single === 'invoice' ? 'Betala med faktura' : options?.ready ? 'Ändra betalsätt' : 'Lägg till betalsätt';
    const start = () => { setError(''); if (single === 'card') choose('card'); else setChoosing(true); };
    const method = !options ? null : invoiceNow
        ? { title: `Faktura i SEK, ${options.invoiceDays} dagars betalningsvillkor.`, hint: 'Fakturan skickas via e-post och betalas till vårt bankkonto.' }
        : options.ready
        ? { title: 'Kortbetalning', hint: 'Kortet debiteras automatiskt vid varje förnyelse.' }
        : { title: 'Inget betalsätt valt', hint: inTrial ? 'Inget kort behövs under provperioden. Välj betalsätt innan den slutar.'
            : entitlements?.billingAccess?.allowed === false ? 'Lägg till ett kort eller välj faktura för att öppna företaget igen.' : 'Lägg till ett kort eller välj faktura.' };

    return <>
        {error && !choosing && <BillingStatus tone="danger" title={options ? 'Betalsättet kunde inte ändras' : 'Betalsätten kunde inte hämtas'} text={error}>
            {!options && <Button size="sm" variant="outline-secondary" onClick={() => { setError(''); setReload(value => value + 1); }}>Försök igen</Button>}
        </BillingStatus>}
        {options && !options.ready && endingSoon && <BillingStatus tone="attention" title={`Provperioden slutar ${date(trialEnd)}`}
            text="Lägg till ett kort eller välj faktura före dess så fortsätter företaget utan avbrott." />}
        {!options && !error && <p role="status" className="billing-muted billing-loading"><Spinner as="span" animation="border" size="sm" />Hämtar betalsätt…</p>}
        {options && <dl className="billing-rows">
            <BillingRow label="Betalsätt" value={method.title} hint={method.hint}
                actions={!locked && choices.length > 0 && <Button size="sm" variant={options.ready ? 'outline-secondary' : 'primary'} disabled={busy} onClick={start}>{actionLabel}</Button>} />
            <BillingRow label="Fakturamottagare" value={options.billingEmail || 'Ingen e-postadress registrerad'}
                hint={options.billingEmail ? 'Fakturor och påminnelser skickas till den här adressen.' : 'Adressen registreras när betalsättet väljs.'}
                actions={onPortal && <button type="button" className="billing-link" disabled={portalBusy} onClick={onPortal}><ExternalLink size={14} aria-hidden="true" />Betalningsuppgifter hos Stripe</button>} />
            {inTrial && <BillingRow label="Provperiod" value={`Kostnadsfritt till ${date(trialEnd)}`} hint="Ingen betalning förrän provperioden är slut." />}
        </dl>}
        {options && <PaymentMethodModal show={choosing} choices={choices} current={options.current} ready={!!options.ready} inTrial={inTrial} days={options.invoiceDays}
            details={details} onChange={setDetails} busy={busy} error={choosing ? error : ''}
            onCancel={() => { setChoosing(false); setError(''); }} onConfirm={choose} />}
    </>;
}
